import { all, call, put, select } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import * as userActions from '../actions/user';
import * as configActions from '../actions/config';
import { getOnRampProvidersFromAccounts } from '../../utils/onRampHelpers';
import * as API from './api';

function buildUserDetailsPayload(user, { detailsPatch, addressPatch } = {}) {
  const existingDetails = user?.userDetails || {};
  const existingAddress = existingDetails?.address || {};
  const nextDetails = { ...existingDetails, ...(detailsPatch || {}) };
  if (addressPatch) {
    nextDetails.address = { ...existingAddress, ...addressPatch };
  }
  return nextDetails;
}

function extractUserDetailsFromResponse(response, fallback) {
  return response?.user?.userDetails ?? response?.User?.UserDetails ?? response ?? fallback;
}

function* getUser() {
  try {
    const user = yield call(API.getUser);
    yield put({
      type: userActions.GET_USER_SUCCESS,
      payload: user,
    });
    try {
      const userConfig = yield call(API.getUserConfig);
      yield put({
        type: configActions.GET_USER_CONFIG_SUCCESS,
        payload: userConfig,
      });
    } catch (_) {
      // supported currencies are optional for the rest of the app
    }
  } catch (error) {
    error.name = 'Could not get User';
    yield put({ type: userActions.GET_USER_ERROR, payload: error });
  }
}

function* updateUser({ payload }) {
  try {
    const user = yield call(API.updateUser, payload);
    yield put({
      type: userActions.UPDATE_USER_SUCCESS,
      payload: user,
    });
  } catch (error) {
    error.name = 'Could not update User';

    if (error.response?.status >= 400 && error.response?.status < 500) {
      if (typeof error.response?.data === 'string' && error.response.data.trim()) {
        error.message = error.response.data.trim();
        error.detail = error.response.data.trim();
      } else if (typeof error.data === 'string' && error.data.trim()) {
        error.message = error.data.trim();
        error.detail = error.data.trim();
      } else if (error.response?.data) {
        const { message: dataMessage, error: dataError } = error.response.data;
        if (dataMessage) {
          error.message = dataMessage;
          error.detail = dataMessage;
        } else if (dataError) {
          error.message = dataError;
          error.detail = dataError;
        }
      }
    }

    yield put({ type: userActions.UPDATE_USER_ERROR, payload: error });
  }
}

function* updateUserDetails({ payload }) {
  try {
    const response = yield call(API.updateUserDetails, payload);
    const userDetails = extractUserDetailsFromResponse(response, payload);
    yield put({
      type: userActions.UPDATE_USER_DETAILS_SUCCESS,
      payload: userDetails,
    });
    yield put({ type: userActions.GET_USER });
  } catch (error) {
    error.name = 'Could not update User Details';
    yield put({ type: userActions.UPDATE_USER_DETAILS_ERROR, payload: error });
  }
}

function* upsertUserOnboardingProfile({ payload }) {
  try {
    const onboardingProfile = yield call(API.upsertUserOnboardingProfile, payload);
    yield put({
      type: userActions.UPSERT_USER_ONBOARDING_PROFILE_SUCCESS,
      payload: {
        ...(onboardingProfile || {}),
        ...(payload || {}),
      },
    });
  } catch (error) {
    error.name = 'Could not update onboarding profile';
    yield put({ type: userActions.UPSERT_USER_ONBOARDING_PROFILE_ERROR, payload: error });
  }
}

function* upsertCompanyOnboardingProfile({ payload }) {
  try {
    const onboardingProfile = yield call(API.upsertCompanyOnboardingProfile, payload);
    yield put({
      type: userActions.UPSERT_COMPANY_ONBOARDING_PROFILE_SUCCESS,
      payload: {
        ...(onboardingProfile || {}),
        ...(payload || {}),
      },
    });
  } catch (error) {
    error.name = 'Could not update company onboarding profile';
    yield put({ type: userActions.UPSERT_COMPANY_ONBOARDING_PROFILE_ERROR, payload: error });
  }
}

function* updateUserAddress({ payload }) {
  try {
    const user = yield select((state) => state.user?.user);
    const fullDetailsPayload = buildUserDetailsPayload(user, { addressPatch: payload || {} });

    const response = yield call(API.updateUserDetails, fullDetailsPayload);
    const userDetails = extractUserDetailsFromResponse(response, fullDetailsPayload);
    yield put({ type: userActions.UPDATE_USER_DETAILS_SUCCESS, payload: userDetails });
    yield put({ type: userActions.GET_USER });
  } catch (error) {
    error.name = 'Could not update User Address';
    yield put({ type: userActions.UPDATE_USER_DETAILS_ERROR, payload: error });
  }
}

function* getBankAccount() {
  try {
    const bankAccount = yield call(API.getBankAccount);
    yield put({
      type: userActions.GET_BANK_ACCOUNT_SUCCESS,
      payload: bankAccount,
    });
    yield put({ type: userActions.GET_ON_RAMP_DEPOSITS, payload: {} });
  } catch (error) {
    error.name = 'Could not get on-ramp account';
    yield put({ type: userActions.GET_BANK_ACCOUNT_ERROR, payload: error });
  }
}

function* fetchOnRampDepositsForProvider(provider) {
  try {
    const deposits = yield call(API.getOnRampDeposits, provider);
    return Array.isArray(deposits) ? deposits : [];
  } catch (error) {
    return [];
  }
}

function* getOnRampDeposits({ payload }) {
  let providers = payload?.providers?.length
    ? payload.providers
    : payload?.provider
      ? [payload.provider]
      : null;

  if (!providers || providers.length === 0) {
    const onRampAccounts = yield select((state) => state.user?.onRampAccounts);
    providers = getOnRampProvidersFromAccounts(onRampAccounts);
  }

  if (!providers.length) {
    yield put({
      type: userActions.GET_ON_RAMP_DEPOSITS_SUCCESS,
      payload: [],
    });
    return;
  }

  const results = yield all(providers.map((provider) => call(fetchOnRampDepositsForProvider, provider)));
  yield put({
    type: userActions.GET_ON_RAMP_DEPOSITS_SUCCESS,
    payload: results.flat(),
  });
}

function* updateCompany({ payload }) {
  try {
    const company = yield call(API.updateCompany, payload);
    yield put({
      type: userActions.UPDATE_COMPANY_SUCCESS,
      payload: company,
    });
  } catch (error) {
    error.name = 'Could not update Company';
    yield put({ type: userActions.UPDATE_COMPANY_ERROR, payload: error });
  }
}

function* updateCompanyAddress({ payload }) {
  try {
    const companyAddress = yield call(API.updateCompanyAddress, payload);
    yield put({
      type: userActions.UPDATE_COMPANY_ADDRESS_SUCCESS,
      payload: companyAddress,
    });
  } catch (error) {
    error.name = 'Could not update Company Address';
    yield put({ type: userActions.UPDATE_COMPANY_ADDRESS_ERROR, payload: error });
  }
}

function* submitOnboarding() {
  try {
    const onboardingRequestId = uuidv4();
    // publish them
    yield put({
      type: userActions.TRACK_ONBOARDING,
      payload: {
        onboardingRequestId,
      },
    });

    const onboardingResponse = yield call(API.submitOnboarding, onboardingRequestId);
    yield put({
      type: userActions.SUBMIT_ONBOARDING_SUCCESS,
      payload: onboardingResponse.steps,
    });
  } catch (error) {
    error.name = 'Onboarding not successful';
    yield put({ type: userActions.SUBMIT_ONBOARDING_ERROR, payload: error });
  }
}

function* updateAllUserData(action) {
  try {
    const { userDetailsChanges, addressChanges } = action.payload;

    // Update User Details
    if (userDetailsChanges.hasChanges) {
      yield* updateUserDetails({ payload: userDetailsChanges.updated });
    }

    // Update Address
    if (addressChanges.hasChanges) {
      yield* updateUserAddress({ payload: addressChanges.updated });
    }

    yield put({ type: userActions.UPDATE_ALL_USER_DATA_SUCCESS });
  } catch (error) {
    error.name = 'Could not update all user data';
    yield put({ type: userActions.UPDATE_ALL_USER_DATA_ERROR, payload: error });
  }
}

function* updateAllCompanyData(action) {
  try {
    const { companyChanges, companyAddressChanges } = action.payload;

    // Update Company
    if (companyChanges.hasChanges) {
      yield* updateCompany({ payload: companyChanges.updated });
    }

    // Update Company Address
    if (companyAddressChanges.hasChanges) {
      yield* updateCompanyAddress({ payload: companyAddressChanges.updated });
    }

    yield put({ type: userActions.UPDATE_ALL_COMPANY_DATA_SUCCESS });
  } catch (error) {
    error.name = 'Could not update all company data';
    yield put({ type: userActions.UPDATE_ALL_COMPANY_DATA_ERROR, payload: error });
  }
}

function* getOnboardingStatus() {
  try {
    const onboardingStatus = yield call(API.getOnboardingStatus);
    yield put({
      type: userActions.GET_ONBOARDING_STATUS_SUCCESS,
      payload: onboardingStatus,
    });
  } catch (error) {
    error.name = 'Could not get onboarding status';
    yield put({ type: userActions.GET_ONBOARDING_STATUS_ERROR, payload: error });
  }
}

function* setOnboardingPhoneNumber({ payload }) {
  try {
    const response = yield call(API.setOnboardingPhoneNumber, payload);
    if (response?.onboardingProgress) {
      yield put({
        type: userActions.GET_ONBOARDING_STATUS_SUCCESS,
        payload: response.onboardingProgress,
      });
    } else {
      yield put(userActions.getOnboardingStatus());
    }
    if (response?.user) {
      yield put({
        type: userActions.GET_USER_SUCCESS,
        payload: response.user,
      });
    }
    yield put({ type: userActions.SET_ONBOARDING_PHONE_NUMBER_SUCCESS });
  } catch (error) {
    error.name = 'Could not set onboarding phone number';
    yield put({ type: userActions.SET_ONBOARDING_PHONE_NUMBER_ERROR, payload: error });
  }
}

function* setOnboardingUserType({ payload }) {
  try {
    const response = yield call(API.setOnboardingUserType, payload);
    if (response?.onboardingProgress) {
      yield put({
        type: userActions.GET_ONBOARDING_STATUS_SUCCESS,
        payload: response.onboardingProgress,
      });
    }
    if (response?.user) {
      yield put({
        type: userActions.GET_USER_SUCCESS,
        payload: response.user,
      });
    }
    yield put({ type: userActions.SET_ONBOARDING_USER_TYPE_SUCCESS });
  } catch (error) {
    error.name = 'Could not set onboarding user type';
    yield put({ type: userActions.SET_ONBOARDING_USER_TYPE_ERROR, payload: error });
  }
}

function* getTermsConditionsSaga() {
  try {
    const terms = yield call(API.getTermsConditions);
    let accepted;
    if (terms?.version) {
      try {
        const acceptance = yield call(API.getTermsConditionsAcceptance, terms.version);
        if (typeof acceptance?.accepted === 'boolean') {
          accepted = acceptance.accepted;
        }
      } catch (_) {
        //
      }
    }

    const payload = typeof accepted === 'boolean' ? { ...terms, accepted } : terms;
    yield put({ type: userActions.GET_TERMS_CONDITIONS_SUCCESS, payload });
  } catch (error) {
    error.name = 'Could not get terms and conditions';
    yield put({ type: userActions.GET_TERMS_CONDITIONS_ERROR, payload: error });
  }
}

function* acceptTermsConditionsSaga({ payload }) {
  try {
    const { version, user: userPayload } = payload || {};
    const response = yield call(API.acceptTermsConditions, version, userPayload);
    if (response?.onboardingProgress) {
      yield put({
        type: userActions.GET_ONBOARDING_STATUS_SUCCESS,
        payload: response.onboardingProgress,
      });
    }
    if (response?.user) {
      yield put({
        type: userActions.GET_USER_SUCCESS,
        payload: response.user,
      });
    }
    yield put({ type: userActions.ACCEPT_TERMS_CONDITIONS_SUCCESS });
  } catch (error) {
    error.name = 'Could not accept terms and conditions';
    yield put({ type: userActions.ACCEPT_TERMS_CONDITIONS_ERROR, payload: error });
  }
}

function* updateUserNames({ payload }) {
  try {
    const apiPayload = {
      id: payload.id,
      email: payload.email,
      firstName: payload.firstName,
      lastName: payload.lastName,
      phoneNumber: payload.phoneNumber,
      userType: payload.userType,
      isOnboarding: payload.isOnboarding,
    };
    const user = yield call(API.updateUserNames, apiPayload);
    yield put({ type: userActions.UPDATE_USER_NAMES_SUCCESS, payload: user });
  } catch (error) {
    error.name = 'Could not update user names';
    yield put({ type: userActions.UPDATE_USER_NAMES_ERROR, payload: error });
  }
}

function* updateIndividualCollectData({ payload }) {
  const { namesPayload, detailsPayload, addressPayload } = payload || {};
  try {
    if (namesPayload) {
      const apiPayload = {
        id: namesPayload.id,
        email: namesPayload.email,
        firstName: namesPayload.firstName,
        lastName: namesPayload.lastName,
        phoneNumber: namesPayload.phoneNumber,
        userType: namesPayload.userType,
        isOnboarding: namesPayload.isOnboarding,
      };
      const user = yield call(API.updateUserNames, apiPayload);
      yield put({ type: userActions.UPDATE_USER_NAMES_SUCCESS, payload: user });
    }
    if (detailsPayload || addressPayload) {
      const user = yield select((state) => state.user?.user);
      const mergedDetails = buildUserDetailsPayload(user, { detailsPatch: detailsPayload, addressPatch: addressPayload });

      const response = yield call(API.updateUserDetails, mergedDetails);
      const userDetails = extractUserDetailsFromResponse(response, mergedDetails);
      yield put({ type: userActions.UPDATE_USER_DETAILS_SUCCESS, payload: userDetails });
      yield put({ type: userActions.GET_USER });
    }
    yield put({ type: userActions.UPDATE_INDIVIDUAL_COLLECT_DATA_SUCCESS });
  } catch (error) {
    error.name = 'Could not update individual collect data';
    yield put({ type: userActions.UPDATE_INDIVIDUAL_COLLECT_DATA_ERROR, payload: error });
  }
}

function* advanceOnboardingStepSaga({ payload: step }) {
  try {
    const response = yield call(API.advanceOnboardingStep, step);
    if (response?.onboardingProgress) {
      yield put({
        type: userActions.GET_ONBOARDING_STATUS_SUCCESS,
        payload: response.onboardingProgress,
      });
    } else {
      yield put(userActions.getOnboardingStatus());
    }
    yield put({ type: userActions.ADVANCE_ONBOARDING_STEP_SUCCESS });
  } catch (error) {
    error.name = 'Could not advance onboarding step';
    yield put({ type: userActions.ADVANCE_ONBOARDING_STEP_ERROR, payload: error });
  }
}

function* saveCompanyCollectDataSaga({ payload }) {
  const { companyPayload, addressPayload } = payload || {};
  try {
    if (!companyPayload) {
      throw new Error('Company payload is required');
    }
    const company = yield call(API.updateCompany, companyPayload);
    yield put({ type: userActions.UPDATE_COMPANY_SUCCESS, payload: company });

    if (addressPayload && Object.keys(addressPayload).length > 0) {
      const companyAddress = yield call(API.updateCompanyAddress, addressPayload);
      yield put({ type: userActions.UPDATE_COMPANY_ADDRESS_SUCCESS, payload: companyAddress });
    }

    yield put(userActions.getUser());
    yield put({ type: userActions.SAVE_COMPANY_COLLECT_DATA_SUCCESS });
  } catch (error) {
    error.name = error.name || 'Could not save company data';
    yield put({ type: userActions.SAVE_COMPANY_COLLECT_DATA_ERROR, payload: error });
  }
}

function* getKycStatusSaga() {
  try {
    const kycStatus = yield call(API.getKycStatus);
    yield put({ type: userActions.GET_KYC_STATUS_SUCCESS, payload: kycStatus });
  } catch (error) {
    error.name = 'Could not get KYC status';
    yield put({ type: userActions.GET_KYC_STATUS_ERROR, payload: error });
  }
}

function* requestKycSaga() {
  try {
    yield call(API.requestKyc);
    yield put({ type: userActions.REQUEST_KYC_SUCCESS });
  } catch (error) {
    error.name = 'Could not request KYC';
    yield put({ type: userActions.REQUEST_KYC_ERROR, payload: error });
  }
}

function* getUserPreferences() {
  try {
    const preferences = yield call(API.getUserPreferences);
    yield put({
      type: userActions.GET_USER_PREFERENCES_SUCCESS,
      payload: preferences,
    });
  } catch (error) {
    error.name = 'Could not get user preferences';
    yield put({ type: userActions.GET_USER_PREFERENCES_ERROR, payload: error });
  }
}

function* upsertUserPreferences({ payload }) {
  try {
    const preferences = yield call(API.upsertUserPreferences, payload);
    yield put({
      type: userActions.UPSERT_USER_PREFERENCES_SUCCESS,
      payload: preferences,
    });
  } catch (error) {
    error.name = 'Could not update user preferences';
    yield put({ type: userActions.UPSERT_USER_PREFERENCES_ERROR, payload: error });
  }
}

const sagas = [
  [userActions.GET_USER, getUser],
  [userActions.UPDATE_USER, updateUser],
  [userActions.UPDATE_USER_DETAILS, updateUserDetails],
  [userActions.UPSERT_USER_ONBOARDING_PROFILE, upsertUserOnboardingProfile],
  [userActions.UPSERT_COMPANY_ONBOARDING_PROFILE, upsertCompanyOnboardingProfile],
  [userActions.UPDATE_USER_NAMES, updateUserNames],
  [userActions.UPDATE_INDIVIDUAL_COLLECT_DATA, updateIndividualCollectData],
  [userActions.ADVANCE_ONBOARDING_STEP, advanceOnboardingStepSaga],
  [userActions.SAVE_COMPANY_COLLECT_DATA, saveCompanyCollectDataSaga],
  [userActions.UPDATE_USER_ADDRESS, updateUserAddress],
  [userActions.UPDATE_COMPANY, updateCompany],
  [userActions.UPDATE_COMPANY_ADDRESS, updateCompanyAddress],
  [userActions.UPDATE_ALL_COMPANY_DATA, updateAllCompanyData],
  [userActions.GET_BANK_ACCOUNT, getBankAccount],
  [userActions.GET_ON_RAMP_DEPOSITS, getOnRampDeposits],
  [userActions.SUBMIT_ONBOARDING, submitOnboarding],
  [userActions.UPDATE_ALL_USER_DATA, updateAllUserData],
  [userActions.GET_ONBOARDING_STATUS, getOnboardingStatus],
  [userActions.SET_ONBOARDING_PHONE_NUMBER, setOnboardingPhoneNumber],
  [userActions.SET_ONBOARDING_USER_TYPE, setOnboardingUserType],
  [userActions.GET_TERMS_CONDITIONS, getTermsConditionsSaga],
  [userActions.ACCEPT_TERMS_CONDITIONS, acceptTermsConditionsSaga],
  [userActions.GET_KYC_STATUS, getKycStatusSaga],
  [userActions.REQUEST_KYC, requestKycSaga],
  [userActions.GET_USER_PREFERENCES, getUserPreferences],
  [userActions.UPSERT_USER_PREFERENCES, upsertUserPreferences],
];

export default sagas;
